import type { ReelProject, SharedDefaults } from '@/types';

export interface EffectiveSettings {
  voiceover: ReelProject['voiceover'];
  captions: ReelProject['captions'];
  music: ReelProject['music'];
  watermark: ReelProject['watermark'];
  export: ReelProject['export'];
}

/**
 * Merges a project's settings with the workspace shared defaults: whenever a
 * setting group's `overridesShared` flag is false, the shared version is used
 * instead of the project's own (possibly stale) copy.
 */
export function resolveEffectiveSettings(project: ReelProject, shared: SharedDefaults): EffectiveSettings {
  return {
    voiceover: project.voiceover.overridesShared
      ? project.voiceover
      : {
          ...shared.voiceover,
          // Per-Reel data that must never come from shared defaults:
          script: project.voiceover.script,
          generatedMediaId: project.voiceover.generatedMediaId,
          generatedDurationSeconds: project.voiceover.generatedDurationSeconds,
          wordTimings: project.voiceover.wordTimings,
          timingSource: project.voiceover.timingSource,
        },
    captions: project.captions.overridesShared ? project.captions : shared.captions,
    music: project.music.overridesShared ? project.music : shared.music,
    watermark: project.watermark.overridesShared ? project.watermark : shared.watermark,
    export: project.export.overridesShared ? project.export : shared.export,
  };
}

/** Returns a copy of the project with effective (resolved) settings applied in place — used at render time. */
export function withEffectiveSettings(project: ReelProject, shared: SharedDefaults): ReelProject {
  const effective = resolveEffectiveSettings(project, shared);
  return { ...project, ...effective };
}
